import React, { useState } from 'react';
import { Loader2, Plus } from 'lucide-react';
import { Button } from '../../ui/Button';
import { ConfirmationModal } from '../../ui/ConfirmationModal';
import { LinkedInConnection } from '../../../types';
import { useLinkedInConnections } from '../../../hooks/useLinkedInConnections';
import {
  LinkedInConnectionCard,
  LinkedInConnectModal,
  LinkedInEmptyState,
} from './linkedin';

/**
 * LinkedIn Settings Section
 *
 * Allows users to manage LinkedIn connections for outreach.
 */
export const LinkedInSection: React.FC = () => {
  const {
    connections,
    isLoading,
    isValidating,
    disconnectingId,
    validateToken,
    disconnect,
    refresh,
  } = useLinkedInConnections();

  const [confirmDisconnect, setConfirmDisconnect] = useState<LinkedInConnection | null>(null);
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [reconnectEmail, setReconnectEmail] = useState<string | undefined>();

  const handleDisconnect = async () => {
    if (!confirmDisconnect) return;
    await disconnect(confirmDisconnect.id);
    setConfirmDisconnect(null);
  };

  const handleOpenConnect = (email?: string) => {
    setReconnectEmail(email);
    setShowConnectModal(true);
  };

  const handleConnectSuccess = () => {
    refresh();
    setShowConnectModal(false);
    setReconnectEmail(undefined);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-13 font-semibold text-neutral-900">LinkedIn Accounts</h3>
          <p className="text-2xs text-neutral-500">
            Manage LinkedIn accounts for connection requests and messages
          </p>
        </div>
        <Button
          id="add-linkedin-account"
          variant="primary"
          size="sm"
          onClick={() => handleOpenConnect()}
          leftIcon={<Plus className="w-3.5 h-3.5" />}
          className="text-xs"
        >
          Add Account
        </Button>
      </div>

      {connections.length === 0 ? (
        <LinkedInEmptyState onConnect={() => handleOpenConnect()} />
      ) : (
        <div className="space-y-3">
          {connections.map((connection) => (
            <LinkedInConnectionCard
              key={connection.id}
              connection={connection}
              isValidating={isValidating === connection.id}
              isDisconnecting={disconnectingId === connection.id}
              onValidate={() => validateToken(connection.id)}
              onDisconnect={() => setConfirmDisconnect(connection)}
              onReconnect={() => handleOpenConnect(connection.linkedin_email)}
            />
          ))}
        </div>
      )}

      <LinkedInConnectModal
        isOpen={showConnectModal}
        onClose={() => {
          setShowConnectModal(false);
          setReconnectEmail(undefined);
        }}
        onSuccess={handleConnectSuccess}
        initialEmail={reconnectEmail}
      />

      <ConfirmationModal
        isOpen={!!confirmDisconnect}
        onClose={() => setConfirmDisconnect(null)}
        onConfirm={handleDisconnect}
        title={confirmDisconnect?.is_active ? 'Disconnect LinkedIn Account' : 'Remove LinkedIn Account'}
        message={
          confirmDisconnect?.is_active
            ? `Are you sure you want to disconnect ${confirmDisconnect?.display_name || confirmDisconnect?.linkedin_email}? You will need to reconnect to send messages from this account.`
            : `Remove ${confirmDisconnect?.display_name || confirmDisconnect?.linkedin_email} from your accounts?`
        }
        confirmText={confirmDisconnect?.is_active ? 'Disconnect' : 'Remove'}
        variant="danger"
        isLoading={!!disconnectingId}
      />
    </div>
  );
};
